<script lang="ts" setup generic="T">
	import { useForm } from 'ant-design-vue/es/form'
	import { objectDiff } from '~/utils/functions'
	import useAccountApi from '~/services/apis/account'
	import { usePlansApi } from '~/services/apis/plan'

	export type CustodianFormType = {
		first_name: string
		last_name: string
		email: string
		phone_number: string
		password: string
	}

	const { required = true, ...props } = defineProps<{
		show: boolean
		planDetails?: PolicyResponse | null
		isPending?: boolean
		categoryName?: string
		note?: string
		additionalForm?: { [id: string]: string | number }
		defaultForm?: Partial<CustodianFormType>
		required?: boolean
		submitFn?: (
			payload: CustodianFormType & { [id: string]: string | number }
		) => Promise<IRequestResponse<unknown> | null>
	}>()

	const emits = defineEmits<{
		(event: 'update:show', param: boolean): void
		(event: 'refresh'): void
	}>()
	const { custodian_id } = useRoute().params
	const { productCategoryType, isSomeObjectValuesEmpty, isSomeObjectValuesFilled } = useUtils()
	const { changeCustodian } = usePlansApi()
	const { confirmPasswordApi } = useAccountApi()
	const isSubmitting = shallowRef(false)
	const newRecordForm = reactive({
		first_name: '',
		last_name: '',
		email: '',
		phone_number: '',
		password: ''
	})
	const { resetFields } = useForm(newRecordForm)

	const isFormValid = () => {
		const { password, ...rest } = newRecordForm
		if (isSomeObjectValuesEmpty(newRecordForm) && required) {
			return false
		} else if (!required && !isSomeObjectValuesFilled(rest)) {
			return false
		}
		return true
	}

	const currentCustodianRecords = computed(() => [
		{
			first_name:
				props.planDetails?.meta?.first_name || props.planDetails?.first_name || props.defaultForm?.first_name,
			last_name:
				props.planDetails?.meta?.last_name || props.planDetails?.last_name || props.defaultForm?.last_name,
			email: props.planDetails?.meta?.email || props.planDetails?.email || props?.defaultForm?.email,
			phone_number:
				props.planDetails?.meta?.phone_number ||
				props.planDetails?.phone_number ||
				props.defaultForm?.phone_number
		}
	])
	const categoryType = computed(() =>
		productCategoryType(
			props.planDetails?.product.productCategory.name || '',
			props.planDetails?.product?.code_name ?? ''
		)
	)
	const recordColumn = computed<TableColumn>(() => [
		{
			key: 'first_name',
			dataIndex: 'first_name',
			title: 'First name'
		},
		{
			key: 'last_name',
			dataIndex: 'last_name',
			title: 'Last Name'
		},
		{
			key: 'email',
			dataIndex: 'email',
			title: 'Email Address',
			customCell: () => ({
				class: 'max-w-[150px] overflow-x-auto  whitespace-nowrap '
			})
		},
		{
			key: 'phone_number',
			dataIndex: 'phone_number',
			title: 'Phone Number'
		}
	])
	const labels = computed(() => {
		let title = `Change ${categoryType.value?.isAuto ? 'vehicle' : props.categoryName} policy holder`
		let subtitle = `Please note that changing this ${
			categoryType.value?.isAuto ? 'vehicle' : props.categoryName
		} policy holder would change who this ${categoryType.value?.isAuto ? 'vehicle' : props.categoryName} is tied to.`

		if (categoryType.value.isHealth || categoryType.value?.isTravel) {
			title = 'Edit Staff Details'
			subtitle = 'Please note that you are about to edit the staff details tied to this policy.'
		}
		return {
			title,
			subtitle
		}
	})
	const submitRecord = async () => {
		if (!(isSomeObjectValuesEmpty(newRecordForm) && required)) {
			resolve(async () => {
				const passwordResult = await confirmPasswordApi({
					password: newRecordForm.password
				})
				if (passwordResult?.data) {
					isSubmitting.value = true
					let res: IRequestResponse | null = null
					if (props.submitFn) {
						const updatedRecord = objectDiff<typeof newRecordForm>(newRecordForm, props.defaultForm || {})
						const { password } = newRecordForm
						res = await props.submitFn({
							...updatedRecord,
							phone_number: replacePhoneNumberToNaija(newRecordForm.phone_number),
							password
						})
					} else if (custodian_id) {
						res = await changeCustodian({
							...newRecordForm,
							policy_id: custodian_id as string,
							pending_policy_id: custodian_id as string,
							is_pending: props.isPending,
							...(props.additionalForm ? props.additionalForm : {})
						})
					} else {
						useNotification().alert({
							message: 'No callback function available',
							status: 500
						})
					}

					if (res && res?.data) {
						useNotification().alert({
							status: res.responseCode,
							message: res.responseText
						})
						emits('refresh')
						emits('update:show', false)
						resetFields()
					}
				}
			}, isSubmitting)
		} else {
			useNotification().alert({
				message: 'Please fill in all required fields',
				status: 400
			})
		}
	}
</script>

<template>
	<div>
		<dialogs-error
			:show="show"
			type="warning"
			:header="labels.title"
			:paragraph="!note ? labels.subtitle : note"
			wrapper-class="bg-gray-50 p-5 rounded-sm space-y-8"
			@update:show="$emit('update:show', false)"
		>
			<template #p>
				<div class="my-5 tracking-wide">
					<p class="mb-6 text-sm font-medium capitalize text-gray-600">
						Current
						{{
							categoryType?.isAuto
								? 'vehicle'
								: categoryType.isHealth || categoryType?.isTravel
									? 'staff details'
									: `${categoryName || ''} policy holder`
						}}
					</p>
					<a-table
						:pagination="false"
						:columns="recordColumn"
						:data-source="currentCustodianRecords"
						class="current"
					/>
				</div>

				<a-form
					:model="newRecordForm"
					:validate-messages="antValidateMessages"
					autocomplete="off"
					@finish="submitRecord"
				>
					<div class="my-5 border-b pb-10">
						<p class="mb-4 font-medium capitalize text-gray-600">
							<span v-show="!categoryType.isHealth && !categoryType?.isTravel">
								New {{ categoryType?.isAuto ? 'vehicle' : categoryName }} Policy Holder
							</span>
							<span v-show="categoryType.isHealth || categoryType?.isTravel">Updated Staff Details</span>
						</p>

						<a-table
							:pagination="false"
							:columns="recordColumn"
							:data-source="currentCustodianRecords"
							class="update"
						>
							<template #bodyCell="{ column }">
								<template v-for="(item, k, i) in newRecordForm" :key="k">
									<a-form-item
										v-if="k !== 'password' && column.key === k"
										class="error-absolut"
										:class="{
											'border-l': i == 0
										}"
										:name="k"
										:required
										:rules="[
											{
												type: (k === 'email' && 'email') || undefined,
												min: (k === 'phone_number' && 11) || undefined,
												max: (k === 'phone_number' && 13) || undefined
											}
										]"
									>
										<a-input
											v-model:value.trim="newRecordForm[k]"
											v-number="k === 'phone_number'"
											:data-testid="k"
											autocomplete="off"
											:inputmode="(k === 'phone_number' && 'numeric') || 'default'"
											class="min-h-[52px] w-full !rounded-none !border-0 !border-r !px-5"
											placeholder="-"
										/>
									</a-form-item>
								</template>
							</template>
						</a-table>
					</div>

					<a-form-item label="Password" class="!mt-5" required name="password">
						<a-input-password
							v-model:value="newRecordForm.password"
							autocomplete="new password"
							data-testid="password"
							placeholder="Enter your password"
							class="custom-input w-full !bg-gray-50 !px-3"
						/>
					</a-form-item>

					<div class="mt-12 flex w-full flex-wrap items-center justify-between gap-4 sm:gap-10">
						<button type="button" name="back" class="default-btn flex-1 justify-center bg-gray-200">
							Back
						</button>

						<a-button
							:disabled="!isFormValid() || isSubmitting"
							:loading="isSubmitting"
							html-type="submit"
							name="continue"
							class="default-btn flex-1 justify-center bg-primary-2"
						>
							Change
						</a-button>
					</div>
				</a-form>
			</template>
			<template #button>
				<span />
			</template>
		</dialogs-error>
	</div>
</template>

<style scoped>
	.current:deep(th.ant-table-cell) {
		@apply !bg-gray-200 !py-2.5 text-sm;
	}

	.current:deep(.ant-table-tbody .ant-table-cell) {
		@apply border-l border-r !border-gray-100 !bg-white text-sm text-gray-300;
	}

	.update:deep(th.ant-table-cell) {
		@apply !bg-purple-50 !py-2.5 text-sm;
	}
	.update:deep(td.ant-table-cell) {
		@apply !px-0 !py-0 text-sm;
	}
	.error-absolute {
		@apply !mb-0;
	}
</style>
